import R from 'ramda';
import * as contactInfoActions from './contactInfoActions';
import * as submissionActions from 'submission/submissionActions';

import {
  completeStep,
  getErrors,
  initialStepState,
  setCompleted,
  setErrors,
  setFieldValue,
  setFields,
  setLoadingInitData,
  setLoadingLocalStorage,
} from 'common/stepReducerUtil';

import {
  validateContactInfo,
  validateContactInfoField,
} from './contactInfoValidation';

const initialContactInfoStepState = {
  fields: {
    firstName: '',
    middleName: '',
    lastName: '',
    orcidId: '',
    studentId: '',
    email: '',
    phoneNumber: '',
    citizenShip: null,
    permanentAddress: {
      addressLineOne: '',
      addressLineTwo: '',
      addressLineThree: '',
      locality: '',
      administrativeArea: null,
      postalCode: '',
      country: null,
    },
  },
  countries: [],
  administrativeAreas: [],
  orcidDialogVisible: false,
};

const initialState = R.merge(initialStepState, initialContactInfoStepState);

const countriesLens = R.lensProp('countries');
const setCountries = R.set(countriesLens);
export const getCountries = R.view(countriesLens);
const getCountryForCode = (code, countries) =>
  R.find(R.propEq('code', code), countries);

const administrativeAreasLens = R.lensProp('administrativeAreas');
const setAdministrativeAreas = R.set(administrativeAreasLens);
export const getAdministrativeAreas = R.view(administrativeAreasLens);
const getAdministrativeAreaForCode = (code, administrativeAreas) =>
  R.find(R.propEq('code', code), administrativeAreas);

const orcidDialogVisibleLens = R.lensProp('orcidDialogVisible');
const setOrcidDialogVisible = R.set(orcidDialogVisibleLens);
export const getOrcidDialogVisible = R.view(orcidDialogVisibleLens);

const administrativeAreaChanged = (administrativeAreaCode, state) =>
  R.pipe(
    setFieldValue(
      ['permanentAddress', 'administrativeArea'],
      getAdministrativeAreaForCode(
        administrativeAreaCode,
        getAdministrativeAreas(state),
      ),
    ),
    setCompleted(false),
  )(state);

const countryChanged = (countryCode, state) =>
  R.pipe(
    setAdministrativeAreas([]),
    setFieldValue(
      ['permanentAddress', 'country'],
      getCountryForCode(countryCode, getCountries(state)),
    ),
    setFieldValue(['permanentAddress', 'administrativeArea'], null),
    setCompleted(false),
  )(state);

const citizenshipChanged = (countryCode, state) =>
  R.pipe(
    setFieldValue(
      ['citizenShip'],
      getCountryForCode(countryCode, getCountries(state)),
    ),
    setCompleted(false),
  )(state);

const fieldBlurred = (field, value, state) =>
  setErrors(validateContactInfoField(field, value, getErrors(state)), state);

const fieldChanged = (path, value, state) =>
  R.pipe(setFieldValue(path, value), setCompleted(false))(state);

const initDataLoaded = (countries, administrativeAreas, state) =>
  R.pipe(
    setLoadingInitData(false),
    setAdministrativeAreas(administrativeAreas),
    setCountries(countries),
  )(state);

const localStorageLoaded = (data, state) =>
  R.pipe(
    setLoadingLocalStorage(false),
    setCompleted(R.isEmpty(data) ? initialState.completed : data.completed),
    setFields(R.isEmpty(data) ? initialState.fields : data.fields),
  )(state);

const cancelSubmission = R.pipe(
  setCompleted(false),
  setErrors(initialState.errors),
  setFields(initialState.fields),
);

const submissionReducer = (state = initialState, action) => {
  switch (action.type) {
    case contactInfoActions.ADMINISTRATIVE_AREA_CHANGED:
      return administrativeAreaChanged(action.administrativeAreaCode, state);

    case contactInfoActions.ADMINISTRATIVE_AREAS_LOADED:
      return setAdministrativeAreas(action.administrativeAreas, state);

    case contactInfoActions.COMPLETE_STEP:
    case submissionActions.SUBMIT:
      return completeStep(validateContactInfo, state);

    case contactInfoActions.COUNTRY_CHANGED:
      return countryChanged(action.countryCode, state);

    case contactInfoActions.CITIZENSHIP_CHANGED:
      return citizenshipChanged(action.countryCode, state);

    case contactInfoActions.FIELD_BLURRED:
      return fieldBlurred(action.field, action.value, state);

    case contactInfoActions.FIELD_CHANGED:
      return fieldChanged(action.path, action.value, state);

    case contactInfoActions.INIT_DATA_LOADED:
      return initDataLoaded(
        action.countries,
        action.administrativeAreas,
        state,
      );

    case contactInfoActions.LOCAL_STORAGE_LOADED:
      return localStorageLoaded(action.data, state);

    case contactInfoActions.SHOW_ORCID_DIALOG:
      return setOrcidDialogVisible(action.show, state);

    case submissionActions.CANCEL_SUBMISSION:
      return cancelSubmission(state);

    default:
      return state;
  }
};

export default submissionReducer;



// WEBPACK FOOTER //
// ./src/contact-info/contactInfoReducer.js